import React, { useRef, useState } from 'react';
import { MdClose, MdUploadFile } from 'react-icons/md';
import { MwButton } from '../ui';
import { apiClient, buildAuthRequestConfig } from '../../utils/apiClient';
import { analyzeImportRows, readTicketImportFile, runPool } from '../../utils/ticketImport';

const API = () => process.env.REACT_APP_SUPPORT_API_BASE_URL;

const MESSAGES = {
    unknownSection: (v) => ({ ar: `القسم "${v}" غير موجود`, en: `Unknown section "${v}"` }),
    unknownProduct: (v) => ({ ar: `المنتج "${v}" غير موجود`, en: `Unknown product "${v}"` }),
    unknownPriority: (v) => ({ ar: `الأولوية "${v}" غير معروفة`, en: `Unknown priority "${v}"` }),
    unknownVisibility: (v) => ({ ar: `نوع التذكرة "${v}" غير معروف`, en: `Unknown ticket type "${v}"` }),
    unknownStatus: (v) => ({ ar: `الحالة "${v}" غير موجودة`, en: `Unknown status "${v}"` }),
    unknownTenant: (v) => ({ ar: `العميل "${v}" غير موجود`, en: `Unknown customer "${v}"` }),
    unknownAssignee: (v) => ({ ar: `الموظف "${v}" غير موجود`, en: `Unknown assignee "${v}"` }),
    invalidDate: (v) => ({ ar: `تاريخ غير صالح "${v}"`, en: `Invalid date "${v}"` }),
    invalidTicketNumber: (v) => ({ ar: `رقم تذكرة غير صالح "${v}"`, en: `Invalid ticket number "${v}"` }),
    duplicateTicketNumber: (v) => ({ ar: `رقم التذكرة ${v} متكرر في الملف`, en: `Ticket ${v} appears more than once` }),
    ticketNotFound: (v) => ({ ar: `التذكرة ${v} غير موجودة أو ليس لديك صلاحية عليها`, en: `Ticket ${v} not found or not allowed` }),
    requiredSubject: () => ({ ar: 'العنوان مطلوب للتذكرة الجديدة', en: 'Subject is required for a new ticket' }),
    requiredTenant: () => ({ ar: 'العميل مطلوب للتذكرة الجديدة', en: 'Customer is required for a new ticket' }),
    requiredSection: () => ({ ar: 'القسم مطلوب للتذكرة الجديدة', en: 'Section is required for a new ticket' }),
    requiredProduct: () => ({ ar: 'المنتج مطلوب للتذكرة الجديدة', en: 'Product is required for a new ticket' }),
};

const ACTION_STYLES = {
    create: 'bg-sky-50 text-sky-700 border-sky-200',
    update: 'bg-amber-50 text-amber-700 border-amber-200',
    unchanged: 'bg-slate-50 text-slate-500 border-slate-200',
    error: 'bg-red-50 text-red-600 border-red-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed: 'bg-red-50 text-red-600 border-red-200',
};

const TicketImportModal = ({ open, onClose, onImported, lookups, isAr }) => {
    const fileInputRef = useRef(null);
    const [step, setStep] = useState('pick'); // pick | analyzing | preview | applying | done
    const [fileName, setFileName] = useState('');
    const [fileError, setFileError] = useState('');
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [plan, setPlan] = useState([]);
    const [showUnchanged, setShowUnchanged] = useState(false);

    if (!open) return null;

    const tr = (key, value) => (MESSAGES[key] ? MESSAGES[key](value)[isAr ? 'ar' : 'en'] : key);
    const actionLabel = {
        create: isAr ? 'إنشاء' : 'Create',
        update: isAr ? 'تعديل' : 'Update',
        unchanged: isAr ? 'بدون تغيير' : 'No change',
        error: isAr ? 'خطأ' : 'Error',
        success: isAr ? 'تم' : 'Done',
        failed: isAr ? 'فشل' : 'Failed',
    };

    const reset = () => {
        setStep('pick');
        setFileName('');
        setFileError('');
        setPlan([]);
        setProgress({ done: 0, total: 0 });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const close = () => {
        if (step === 'analyzing' || step === 'applying') return;
        const hadChanges = step === 'done';
        reset();
        onClose();
        if (hadChanges) onImported?.();
    };

    // بندوّر بالرقم عن طريق /admin?q= (نفس صلاحيات صفحة التذاكر)، وناخد اللي رقمها مطابق بالظبط.
    const findTicketByNumber = async (ticketNumber) => {
        const params = new URLSearchParams({ q: String(ticketNumber), limit: '20', page: '1' });
        const response = await apiClient.get(`${API()}/admin?${params.toString()}`, buildAuthRequestConfig());
        const list = Array.isArray(response?.data?.data) ? response.data.data : [];
        return list.find((ticket) => Number(ticket?.ticketNumber) === ticketNumber) || null;
    };

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setFileError('');
        setStep('analyzing');
        try {
            const { rows } = await readTicketImportFile(file);
            if (!rows.length) {
                setFileError(isAr ? 'الملف لا يحتوي على صفوف تذاكر' : 'The file has no ticket rows');
                setStep('pick');
                return;
            }
            setProgress({ done: 0, total: rows.length });
            const results = await analyzeImportRows(rows, lookups, {
                findTicketByNumber,
                t: tr,
                onProgress: (done, total) => setProgress({ done, total }),
            });
            setPlan(results);
            setStep('preview');
        } catch (err) {
            console.error('Ticket import read failed:', err);
            setFileError(
                err?.message === 'NO_HEADER'
                    ? (isAr ? 'لم يتم العثور على عمود "رقم التذكرة" — استخدم ملف التصدير من هذه الصفحة' : 'Could not find a "Ticket #" column — use a file exported from this page')
                    : (isAr ? 'تعذر قراءة الملف، تأكد أنه ملف Excel (.xlsx)' : 'Could not read the file, make sure it is an Excel (.xlsx) file')
            );
            setStep('pick');
        }
    };

    const apply = async () => {
        const actionable = plan.map((row, index) => ({ row, index })).filter(({ row }) => row.action === 'create' || row.action === 'update');
        if (!actionable.length) return;
        setStep('applying');
        setProgress({ done: 0, total: actionable.length });
        const next = [...plan];
        let done = 0;
        await runPool(actionable, 3, async ({ row, index }) => {
            try {
                if (row.action === 'create') {
                    const response = await apiClient.post(`${API()}`, row.body, buildAuthRequestConfig());
                    if (response?.data?.status !== 1) throw new Error(response?.data?.message || 'Failed');
                    next[index] = { ...row, result: 'success', createdNumber: response?.data?.data?.ticketNumber };
                } else {
                    if (row.body) {
                        const response = await apiClient.put(`${API()}/admin/${row.ticketId}`, row.body, buildAuthRequestConfig());
                        if (response?.data?.status !== 1) throw new Error(response?.data?.message || 'Failed');
                    }
                    if (row.statusId) {
                        const response = await apiClient.put(`${API()}/admin/${row.ticketId}/status`, { statusId: row.statusId }, buildAuthRequestConfig());
                        if (response?.data?.status !== 1) throw new Error(response?.data?.message || 'Failed');
                    }
                    next[index] = { ...row, result: 'success' };
                }
            } catch (err) {
                next[index] = { ...row, result: 'failed', resultError: err?.response?.data?.message || err?.message || 'Failed' };
            }
            done += 1;
            setProgress({ done, total: actionable.length });
        });
        setPlan(next);
        setStep('done');
    };

    const counts = plan.reduce((acc, row) => {
        acc[row.action] = (acc[row.action] || 0) + 1;
        if (row.result) acc[row.result] = (acc[row.result] || 0) + 1;
        return acc;
    }, {});
    const actionableCount = (counts.create || 0) + (counts.update || 0);
    const visibleRows = plan.filter((row) => showUnchanged || row.action !== 'unchanged');

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-700/75 p-3 md:p-6" dir={isAr ? 'rtl' : 'ltr'}>
            <div className="flex flex-col w-full max-w-5xl max-h-[calc(100vh-32px)] bg-white rounded-xl overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <span className="text-sm font-bold text-slate-800">{isAr ? 'استيراد تذاكر من Excel' : 'Import tickets from Excel'}</span>
                    <button type="button" onClick={close} className="text-slate-500 hover:text-slate-800" aria-label="close"><MdClose size={22} /></button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 p-4 flex flex-col gap-4">
                    {step === 'pick' && (
                        <>
                            <div className="text-sm text-slate-600 leading-7 bg-white border border-slate-200 rounded-xl p-4">
                                <p className="font-bold text-slate-800 mb-1">{isAr ? 'طريقة الاستخدام' : 'How it works'}</p>
                                <ul className="list-disc ps-5">
                                    <li>{isAr ? 'حمّل ملف Excel من هذه الصفحة (كل النتائج أو التذاكر المحددة) وعدّل عليه.' : 'Download an Excel file from this page (all results or selected tickets) and edit it.'}</li>
                                    <li>{isAr ? 'صف فيه رقم تذكرة = تعديل التذكرة دي. الخلية الفاضية تعني بدون تغيير.' : 'A row with a ticket number updates that ticket. Empty cells mean "no change".'}</li>
                                    <li>{isAr ? 'صف بدون رقم تذكرة = تذكرة جديدة (العنوان، العميل، القسم، المنتج مطلوبين).' : 'A row without a ticket number creates a new ticket (subject, customer, section and product are required).'}</li>
                                    <li>{isAr ? 'يمكن إضافة عمود "الوصف" لمحتوى التذكرة الجديدة. أعمدة التواريخ و"آخر تعديل" تُتجاهل.' : 'You can add a "Description" column for new tickets. Created/updated dates and "Last modified by" are ignored.'}</li>
                                    <li>{isAr ? 'هتشوف معاينة بكل التغييرات قبل التنفيذ.' : 'You will see a preview of every change before anything is saved.'}</li>
                                </ul>
                            </div>
                            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl bg-white p-8 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors">
                                <MdUploadFile size={36} className="text-emerald-600" />
                                <span className="text-sm font-bold text-slate-700">{isAr ? 'اختر ملف Excel (.xlsx)' : 'Choose an Excel file (.xlsx)'}</span>
                                {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
                                <input ref={fileInputRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={handleFile} />
                            </label>
                            {fileError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{fileError}</div>}
                        </>
                    )}

                    {(step === 'analyzing' || step === 'applying') && (
                        <div className="flex flex-col items-center justify-center gap-3 py-16">
                            <span className="text-sm font-bold text-slate-700">
                                {step === 'analyzing' ? (isAr ? 'جاري فحص الملف...' : 'Checking the file...') : (isAr ? 'جاري حفظ التغييرات...' : 'Saving changes...')}
                            </span>
                            <div className="w-72 h-2 rounded-full bg-slate-200 overflow-hidden">
                                <div className="h-full bg-emerald-600 transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
                            </div>
                            <span className="text-xs text-slate-500 tabular-nums">{progress.done} / {progress.total}</span>
                        </div>
                    )}

                    {(step === 'preview' || step === 'done') && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-slate-500">{fileName}</span>
                                {step === 'preview' ? (
                                    ['create', 'update', 'unchanged', 'error'].map((key) => (
                                        <span key={key} className={`px-2 py-0.5 rounded-full border font-semibold ${ACTION_STYLES[key]}`}>{actionLabel[key]}: {counts[key] || 0}</span>
                                    ))
                                ) : (
                                    ['success', 'failed'].map((key) => (
                                        <span key={key} className={`px-2 py-0.5 rounded-full border font-semibold ${ACTION_STYLES[key]}`}>{actionLabel[key]}: {counts[key] || 0}</span>
                                    ))
                                )}
                                {counts.unchanged > 0 && (
                                    <label className="flex items-center gap-1.5 ms-auto text-slate-600 cursor-pointer">
                                        <input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} />
                                        {isAr ? 'عرض الصفوف بدون تغيير' : 'Show unchanged rows'}
                                    </label>
                                )}
                            </div>
                            <div className="table-scroll overflow-x-auto rounded-xl border border-slate-200 bg-white">
                                <table className="w-full text-xs text-start">
                                    <thead className="bg-slate-100 text-slate-700">
                                        <tr>
                                            <th className="px-3 py-2 text-start font-bold whitespace-nowrap">{isAr ? 'صف' : 'Row'}</th>
                                            <th className="px-3 py-2 text-start font-bold whitespace-nowrap">{isAr ? 'رقم التذكرة' : 'Ticket #'}</th>
                                            <th className="px-3 py-2 text-start font-bold">{isAr ? 'العنوان' : 'Subject'}</th>
                                            <th className="px-3 py-2 text-start font-bold whitespace-nowrap">{isAr ? 'الإجراء' : 'Action'}</th>
                                            <th className="px-3 py-2 text-start font-bold">{isAr ? 'التفاصيل' : 'Details'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {visibleRows.map((row) => {
                                            const badge = row.result || row.action;
                                            return (
                                                <tr key={row.rowNumber} className="align-top">
                                                    <td className="px-3 py-2 text-slate-400 tabular-nums">{row.rowNumber}</td>
                                                    <td className="px-3 py-2 font-mono text-slate-800 whitespace-nowrap">{row.ticketNumber || row.createdNumber || (isAr ? 'جديدة' : 'New')}</td>
                                                    <td className="px-3 py-2 text-slate-700 max-w-[260px] truncate" title={row.subject}>{row.subject || '-'}</td>
                                                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap ${ACTION_STYLES[badge]}`}>{actionLabel[badge]}</span></td>
                                                    <td className="px-3 py-2 text-slate-600">
                                                        {row.resultError && <div className="text-red-600">{row.resultError}</div>}
                                                        {(row.errors || []).map((error) => <div key={error} className="text-red-600">{error}</div>)}
                                                        {(row.changes || []).map((change) => (
                                                            <div key={change.field}>
                                                                <span className="font-semibold text-slate-700">{change.field}:</span>{' '}
                                                                <span className="line-through text-slate-400">{change.from}</span>{' → '}
                                                                <span className="text-slate-800">{change.to}</span>
                                                            </div>
                                                        ))}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {!visibleRows.length && (
                                            <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">{isAr ? 'لا توجد تغييرات في الملف' : 'No changes in the file'}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-white">
                    {step === 'preview' && (
                        <>
                            <MwButton type="secondary" onClick={reset}>{isAr ? 'اختيار ملف آخر' : 'Choose another file'}</MwButton>
                            <MwButton type="saveBtn" onClick={apply} disabled={!actionableCount}>
                                {isAr ? `تنفيذ ${actionableCount} تغيير` : `Apply ${actionableCount} change${actionableCount === 1 ? '' : 's'}`}
                            </MwButton>
                        </>
                    )}
                    {(step === 'pick' || step === 'done') && (
                        <MwButton type="cancelBtn" onClick={close}>{isAr ? 'إغلاق' : 'Close'}</MwButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TicketImportModal;
